import axios, { AxiosError } from "axios";

const BASE = import.meta.env.VITE_API_BASE_URL || "https://api.travelhug.ai/";

// localStorage keys
export const K = {
    deviceId: "th_device_id",
    token: "th_token",
    exp: "th_token_expires_at",
    refreshToken: "th_refresh_token",
    guestToken: "th_guest_token",
    guestRefreshToken: "th_guest_refresh_token",
    guestExp: "th_guest_token_expires_at",
};

// persist a device id
export function getOrCreateDeviceId() {
    let id = localStorage.getItem(K.deviceId);
    if (!id) {
        id =
            (crypto.randomUUID && crypto.randomUUID()) ||
            `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(K.deviceId, id);
    }
    return id;
}

// is stored main token still valid?
export function isMainTokenValid() {
    const token = localStorage.getItem(K.token);
    const exp = localStorage.getItem(K.exp);
    if (!token || !exp) return false;
    const expMs = Date.parse(exp);
    return Number.isFinite(expMs) && expMs - Date.now() > 30_000; // 30s skew
}

// is stored guest token still valid?
export function isGuestTokenValid() {
    const token = localStorage.getItem(K.guestToken);
    const exp = localStorage.getItem(K.guestExp);
    if (!token || !exp) return false;
    const expMs = Date.parse(exp);
    return Number.isFinite(expMs) && expMs - Date.now() > 30_000; // 30s skew
}

// fetch new device-scoped token (requires X-Device-Id)
export async function fetchDeviceToken() {
    try {
        const deviceId = getOrCreateDeviceId();
        const res = await axios.post(
            `${BASE}api/v1/auth/guest-login`,
            {},
            { headers: { "X-Device-Id": deviceId, Accept: "application/json" } }
        );
        const { access_token, expires_at, refresh_token } = res.data || {};
        if (!access_token) throw new Error("Auth token missing from response");

        localStorage.setItem(K.guestToken, access_token);
        if (refresh_token) localStorage.setItem(K.guestRefreshToken, refresh_token);
        if (expires_at) {
            // Convert numeric timestamp (likely seconds) to ISO string for Date.parse compatibility
            const expTime = typeof expires_at === "number" ? expires_at * 1000 : expires_at;
            localStorage.setItem(K.guestExp, new Date(expTime).toISOString());
        }
        return access_token;
    } catch (error: unknown) {
        const axiosError = error as AxiosError;
        if (axiosError?.status === 401) {
            localStorage.clear();
            window.location.reload();
        }
    }
}

export async function getAuthToken() {
    if (isMainTokenValid()) return localStorage.getItem(K.token);
    if (isGuestTokenValid()) return localStorage.getItem(K.guestToken);
    return await fetchDeviceToken();
}

export function clearAuthToken() {
    localStorage.removeItem(K.token);
    localStorage.removeItem(K.exp);
    localStorage.removeItem(K.refreshToken);
}

export async function refreshAuthToken() {
    const refreshToken = localStorage.getItem(K.refreshToken);
    if (!refreshToken) return null;

    try {
        // Use a new axios instance to avoid interceptors loops
        const res = await axios.post(
            `${BASE}api/v1/auth/refresh`,
            { refresh_token: refreshToken },
            {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            }
        );
        const { access_token, expires_at, refresh_token: newRefreshToken } = res.data || {};

        if (access_token) {
            localStorage.setItem(K.token, access_token);
            if (expires_at) {
                const expTime = typeof expires_at === "number" ? expires_at * 1000 : expires_at;
                localStorage.setItem(K.exp, new Date(expTime).toISOString());
            }
            if (newRefreshToken) {
                localStorage.setItem(K.refreshToken, newRefreshToken);
            }
            return access_token;
        }
    } catch (error) {
        // If refresh fails, let the caller handle the logout/redirect
        console.error("Failed to refresh token", error);
    }
    return null;
}
